import { useState } from 'react';
import { X, CheckCircle, DollarSign, Clock, FileText } from 'lucide-react';
import { Quotation } from '../../types/maintenance';

interface ApproveQuotationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (quotationId: string, notes?: string) => Promise<void>;
  quotations: Quotation[];
  loading?: boolean;
}

export default function ApproveQuotationModal({
  isOpen,
  onClose,
  onConfirm,
  quotations,
  loading = false
}: ApproveQuotationModalProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');

  const handleClose = () => {
    setSelectedId(null);
    setNotes('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setError('');

    if (!selectedId) {
      setError('Selecione uma cotação para aprovar.');
      return;
    }

    try {
      await onConfirm(selectedId, notes.trim() || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao aprovar cotação.');
    }
  };

  const sortedQuotations = [...quotations].sort((a, b) => a.quotedValue - b.quotedValue);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" onClick={handleClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <CheckCircle className="h-5 w-5 mr-2 text-blue-600" />
              Aprovar Cotação
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
              disabled={loading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Selecione a cotação que pretende aprovar. As cotações estão ordenadas por valor (menor para maior).
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Quotations List */}
          <div className="space-y-3 mb-6">
            {sortedQuotations.map((q, index) => (
              <div
                key={q.id}
                onClick={() => !loading && setSelectedId(q.id)}
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  selectedId === q.id
                    ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-900">
                        {index + 1}. {q.partnerName}
                      </span>
                      {index === 0 && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                          Menor valor
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 space-y-0.5">
                      <div>{q.contactEmail}</div>
                      {q.contactPhone && <div>{q.contactPhone}</div>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center text-lg font-bold text-gray-900">
                      <DollarSign className="h-4 w-4 mr-0.5 text-gray-400" />
                      €{Number(q.quotedValue).toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 mt-2 text-xs text-gray-500">
                  {q.deliveryTime && (
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {q.deliveryTime}
                    </span>
                  )}
                  {q.notes && (
                    <span className="flex items-center">
                      <FileText className="h-3 w-3 mr-1" />
                      {q.notes}
                    </span>
                  )}
                </div>

                {/* Selection indicator */}
                <div className="mt-2 flex items-center">
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    selectedId === q.id ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                  }`}>
                    {selectedId === q.id && (
                      <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <span className="ml-2 text-xs text-gray-500">
                    {selectedId === q.id ? 'Selecionada' : 'Clique para selecionar'}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas de Aprovação (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              placeholder="Justificativa da escolha..."
              disabled={loading}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!selectedId || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <span>Aprovando...</span>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Aprovar Cotação
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
